// BUILD-046 task 8.19 (supporting model): the flow that runs Done and Undo on a My tasks row (design "Done (Req 8)"; Req 8.2, 8.3, 8.11, 8.13).
//
// doneModel.ts is the per-item state machine (idle -> saving -> idle | failed, and Undo); this module runs it against the typed client:
//  - the state changes BEFORE the request is sent (`activateDone` is pure and synchronous), so the control is disabled at once and a second
//    activation while the request runs sends nothing;
//  - Done sends no body (workApi.complete) and is bounded at 10 s by the client; a failure of any kind leaves the task open in the status it had
//    and says the task was not completed (the row keeps showing the prior status, nothing here writes a status);
//  - on success the caller merges the SERVER's item into the list and fires `shared-tasks:changed` (the counts refresh); nothing is guessed;
//  - Undo is the existing restore route, offered right after a success;
//  - an answer dropped because the Caller changed is neither a success nor a failure (the Caller change resets the Done state).
//
// Pure module apart from the injected api. Runtime imports: the Done model. Erasable TypeScript only.

import { activateDone, activateUndo, doneFailed, doneSucceeded, undoFailed, undoSucceeded } from './doneModel';
import type { DoneState } from './doneModel';

export interface DoneApi {
  complete: (id: string) => Promise<{ item: unknown }>;
  restore: (id: string) => Promise<{ item: unknown }>;
}

export interface DoneStore {
  get: () => DoneState;
  set: (next: DoneState) => void;
}

export type DoneOutcome = { kind: 'ignored' } | { kind: 'done'; item: unknown } | { kind: 'failed' } | { kind: 'dropped' };
export type UndoOutcome = { kind: 'ignored' } | { kind: 'undone'; item: unknown } | { kind: 'failed' } | { kind: 'dropped' };

function isStale(thrown: unknown): boolean {
  return thrown !== null && typeof thrown === 'object' && (thrown as { kind?: unknown }).kind === 'stale';
}

/** Done for one row (`status` is the status the row shows now). */
export async function performDone(api: DoneApi, store: DoneStore, id: string, status: string): Promise<DoneOutcome> {
  const step = activateDone(store.get(), id, status);
  if (!step.send) return { kind: 'ignored' };
  store.set(step.state);
  try {
    const response = await api.complete(id);
    store.set(doneSucceeded(store.get(), id));
    return { kind: 'done', item: response.item };
  } catch (thrown) {
    if (isStale(thrown)) return { kind: 'dropped' };
    store.set(doneFailed(store.get(), id, thrown));
    return { kind: 'failed' };
  }
}

/** Undo after a successful Done (the existing restore route). */
export async function performUndo(api: DoneApi, store: DoneStore, id: string): Promise<UndoOutcome> {
  const step = activateUndo(store.get(), id);
  if (!step.send) return { kind: 'ignored' };
  store.set(step.state);
  try {
    const response = await api.restore(id);
    store.set(undoSucceeded(store.get(), id));
    return { kind: 'undone', item: response.item };
  } catch (thrown) {
    if (isStale(thrown)) return { kind: 'dropped' };
    store.set(undoFailed(store.get(), id, thrown));
    return { kind: 'failed' };
  }
}
